import { initializeApp } from 'firebase/app';
import { getMessaging, getToken, onMessage, isSupported } from 'firebase/messaging';
import { registerDeviceToken, updateNotificationStatus } from '../api/NotificationApi';

// Access Firebase config directly from environment variables
const firebaseConfig = {
  apiKey: import.meta.env.VITE_FIREBASE_API_KEY,
  authDomain: import.meta.env.VITE_FIREBASE_AUTH_DOMAIN,
  projectId: import.meta.env.VITE_FIREBASE_PROJECT_ID,
  storageBucket: import.meta.env.VITE_FIREBASE_STORAGE_BUCKET,
  messagingSenderId: import.meta.env.VITE_FIREBASE_MESSAGING_SENDER_ID,
  appId: import.meta.env.VITE_FIREBASE_APP_ID
};

const vapidKey = import.meta.env.VITE_FIREBASE_VAPID_KEY;

// Initialize Firebase
let messaging = null;

// Initialize messaging if supported
const initializeMessaging = async () => {
  try {
    if (await isSupported()) {
      const app = initializeApp(firebaseConfig);
      messaging = getMessaging(app);
      console.log("Firebase messaging initialized successfully");
      
      // Update service worker with config if it exists
      if (navigator.serviceWorker && navigator.serviceWorker.controller) {
        navigator.serviceWorker.controller.postMessage({
          type: 'FIREBASE_CONFIG',
          config: firebaseConfig
        });
      }
      
      return true;
    } else {
      console.log("Firebase messaging is not supported in this browser");
      return false;
    }
  } catch (error) {
    console.error("Error initializing Firebase messaging:", error);
    return false;
  }
};

// Initialize Firebase - called when the module is imported
initializeMessaging();

/**
 * Check browser's notification permission status
 * 
 * @returns {string} - 'granted', 'denied', 'default', or 'unsupported'
 */
export const getBrowserPermissionStatus = () => {
  if (!('Notification' in window)) {
    console.log("Notifications not supported in this browser");
    return 'unsupported';
  }
  console.log("Current browser notification permission:", Notification.permission);
  return Notification.permission;
};

/**
 * Listen for changes in notification permission status and update backend
 */
export const setupPermissionChangeListener = () => {
  // Store initial permission state
  let previousPermission = Notification.permission;
  
  // Check permission status periodically (browsers don't have a native event for this)
  const permissionCheckInterval = setInterval(async () => {
    const currentPermission = Notification.permission;
    
    // If permission changed
    if (currentPermission !== previousPermission) {
      console.log(`Notification permission changed from ${previousPermission} to ${currentPermission}`);
      previousPermission = currentPermission;
      
      // If now granted, register token
      if (currentPermission === 'granted') {
        await registerDeviceWithToken();
      } 
      // If now denied, clean up tokens and update backend
      else if (currentPermission === 'denied') {
        cleanupNotificationToken();
      }
    }
  }, 2000); // Check every 2 seconds
  
  // Return function to clear interval when component unmounts
  return () => clearInterval(permissionCheckInterval);
};

/**
 * Request notification permission using browser's native UI
 * and register device if granted
 * 
 * @returns {Promise<Object>} Result of the permission request
 */
export const requestNotificationPermission = async () => {
  console.log("=== START: requestNotificationPermission ===");
  
  // Check if user is authenticated first
  const isAuthenticated = !!localStorage.getItem('access_token');
  console.log("User authenticated:", isAuthenticated);
  
  if (!isAuthenticated) {
    console.log("User not authenticated, cannot request notification permission");
    return {
      success: false,
      message: 'Authentication required to enable notifications'
    };
  }
  
  try {
    // Check if notifications are supported
    if (!('Notification' in window)) {
      console.log("Notifications not supported in this browser");
      return {
        success: false,
        message: 'Notifications not supported in this browser'
      };
    }
    
    // Initialize messaging if not already done
    if (!messaging) {
      const initResult = await initializeMessaging();
      if (!initResult) {
        return {
          success: false,
          message: 'Failed to initialize Firebase messaging'
        };
      }
    }
    
    // Check if already granted
    console.log("Current permission:", Notification.permission);
    if (Notification.permission === 'granted') {
      console.log("Permission already granted, registering device");
      // Get token and register device
      return await registerDeviceWithToken();
    }
    
    // If denied, can't request again programmatically
    if (Notification.permission === 'denied') {
      console.log("Permission already denied by user");
      // Update backend about denied status
      await updateNotificationStatus(false);
      return {
        success: false,
        message: 'Notification permission was denied'
      };
    }
    
    // Request permission via browser native UI
    console.log("Requesting notification permission via browser native UI...");
    const permission = await Notification.requestPermission();
    console.log("Permission after request:", permission);
    
    if (permission === 'granted') {
      // Register device with token
      console.log("Permission granted, registering device with backend");
      return await registerDeviceWithToken();
    } else {
      console.log("Permission not granted:", permission);
      // Update backend about denied status if explicitly denied
      if (permission === 'denied') {
        await updateNotificationStatus(false);
      }
      return {
        success: false,
        message: `Notification permission ${permission}`
      };
    }
  } catch (error) {
    console.error('Error requesting notification permission:', error);
    return {
      success: false,
      message: 'Error requesting notification permission',
      error
    };
  } finally {
    console.log("=== END: requestNotificationPermission ===");
  }
};

/**
 * Gets FCM token and registers device with backend
 * 
 * @returns {Promise<Object>} Registration result
 */
const registerDeviceWithToken = async () => {
  console.log("=== START: registerDeviceWithToken ===");
  
  // Check if messaging is initialized
  if (!messaging) {
    console.log("Messaging not initialized, attempting to initialize");
    const initResult = await initializeMessaging();
    if (!initResult) {
      return {
        success: false,
        message: 'Firebase messaging not supported in this browser'
      };
    }
  }
  
  try {
    // Get FCM token - use vapidKey directly from env
    const token = await getToken(messaging, {
      vapidKey: vapidKey
    });
    
    console.log('FCM Token obtained successfully');
    
    // Save token to localStorage BEFORE trying to register with backend
    localStorage.setItem('fcm_token', token);
    
    // Register with backend
    console.log("Registering token with backend");
    const result = await registerDeviceToken(token, true);
    console.log("Backend registration result:", result.success ? "Success" : "Failed");
    
    if (result.success) {
      // Mark as sent to backend
      localStorage.setItem('fcm_token_sent', 'true');
      console.log("Token successfully registered with backend");
      
      return {
        success: true,
        token,
        message: 'Notification permission granted and device registered'
      };
    } else {
      console.error("Failed to register token with backend:", result.error);
      return {
        success: false,
        token,
        message: 'Failed to register device with backend'
      };
    }
  } catch (error) {
    console.error('Error getting token or registering device:', error);
    return {
      success: false,
      message: 'Error registering device',
      error
    };
  } finally {
    console.log("=== END: registerDeviceWithToken ===");
  }
};

/**
 * Handle foreground messages
 */
export const setupMessageListener = () => {
  if (!messaging) return;
  
  onMessage(messaging, (payload) => {
    console.log('Message received in foreground:', payload);
    
    // Display a notification if browser supports it
    if ('Notification' in window && Notification.permission === 'granted') {
      const notificationTitle = payload.notification?.title || 'New Notification';
      const notificationOptions = {
        body: payload.notification?.body || '',
        icon: '/images/logo.png',
        data: payload.data || {}
      };
      
      // Create and show notification
      new Notification(notificationTitle, notificationOptions);
    }
  });
};

/**
 * Cleanup token when permissions are denied
 */
export const cleanupNotificationToken = () => {
  console.log("=== START: cleanupNotificationToken ===");
  
  try {
    // Get the current permission
    const currentPermission = getBrowserPermissionStatus();
    
    // If denied, clean up the token data
    if (currentPermission === 'denied') {
      // Save the old token for potential backend update
      const oldToken = localStorage.getItem('fcm_token');
      
      // Remove the token from localStorage
      localStorage.removeItem('fcm_token');
      localStorage.removeItem('fcm_token_sent');
      
      console.log("Removed FCM tokens from localStorage due to denied permission");
      
      // If we had a token and are authenticated, try to update backend
      if (oldToken && localStorage.getItem('access_token')) {
        updateNotificationStatus(false)
          .then(result => console.log("Backend notification status update result:", result))
          .catch(err => console.error("Failed to update backend notification status:", err));
      }
      
      return true;
    }
    
    return false;
  } catch (error) {
    console.error("Error cleaning up notification token:", error);
    return false;
  } finally {
    console.log("=== END: cleanupNotificationToken ===");
  }
};

export default { 
  getBrowserPermissionStatus, 
  requestNotificationPermission, 
  setupMessageListener,
  setupPermissionChangeListener,
  cleanupNotificationToken
};
